import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminAPI } from '../components/admin/AdminUtils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { socialLinks } from '../data/mock';
import { Utensils, CheckCircle } from 'lucide-react';

const groupByType = (dishes) => {
  const groups = { starter: [], main: [], dessert: [] };
  (dishes || []).forEach(d => {
    const t = (d.type || 'main').toLowerCase();
    if (!groups[t]) groups[t] = [];
    groups[t].push(d);
  });
  return groups;
};

const formatEUR = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

const TableOrder = () => {
  const { slug } = useParams();
  const [tableData, setTableData] = useState(null);
  const [event, setEvent] = useState(null);
  const [menu, setMenu] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [siteMediaEnabled, setSiteMediaEnabled] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminAPI.get(`/tables/public/${slug}`);
        setTableData(res.data.table);
        setEvent(res.data.event);
        if (res.data.event?.id) {
          const m = await adminAPI.get(`/menus/${res.data.event.id}`);
          setMenu(m.data.menu || { dishes: [] });
        }
      } catch (e) {
        setTableData(null);
        setEvent(null);
        setMenu(null);
      }
    };
    load();
  }, [slug]);

  useEffect(() => {
    let active = true;

    const loadSiteMediaConfig = async () => {
      try {
        const res = await adminAPI.get(`/site-media/public-config`);
        const enabled = res.data?.enabled !== false;
        if (!active) return;
        setSiteMediaEnabled(enabled);
        try {
          localStorage.setItem('siteMediaEnabled', enabled ? 'true' : 'false');
        } catch {}
      } catch {
        try {
          const v = localStorage.getItem('siteMediaEnabled');
          if (active) {
            setSiteMediaEnabled(v === null ? true : v === 'true');
          }
        } catch {}
      }
    };

    const onStorage = (e) => {
      if (e.key === 'siteMediaEnabled') {
        try {
          const v = localStorage.getItem('siteMediaEnabled');
          setSiteMediaEnabled(v === null ? true : v === 'true');
        } catch {}
      }
    };

    loadSiteMediaConfig();

    window.addEventListener('storage', onStorage);

    return () => {
      active = false;
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const groups = useMemo(() => groupByType(menu?.dishes || []), [menu]);

  const setQty = (name, val) => {
    const q = Math.max(0, parseInt(val) || 0);
    setQuantities(prev => ({ ...prev, [name]: q }));
  };

  const submitOrder = async () => {
    const items = Object.entries(quantities)
      .filter(([_, q]) => q > 0)
      .map(([name, q]) => {
        const dish = (menu?.dishes || []).find(d => d.name === name);
        const price = dish && typeof dish.price === 'number' ? dish.price : 0;
        return { name, quantity: q, price };
      });
    if (!items.length) return;
    setSubmitting(true);
    try {
      await adminAPI.post(`/orders`, { tableSlug: slug, items, comment: (comment || '').trim() || undefined });
      setSubmitted(true);
      setQuantities({});
      setComment('');
      setTimeout(() => setSubmitted(false), 3000);
    } catch (e) {
      alert('Erreur lors de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Header forceOpaque siteMediaEnabled={siteMediaEnabled} showReserve={false} socialLinks={socialLinks} />
      <main className="px-4 pt-28 pb-24 mx-auto max-w-5xl sm:px-6 lg:px-8">
        <div>
          {(!tableData || !event) ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <p className="text-slate-300">Table introuvable.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex gap-3 items-center mb-6">
                <Badge className="bg-slate-700 text-slate-200 border-slate-600">Vol.{event.volume}</Badge>
                <h1 className="text-2xl font-bold text-white truncate">{event.theme}</h1>
              </div>
              <section className="mt-2 space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-white">Table {tableData.name}</h2>
                  <Badge className="bg-slate-700 text-slate-200 border-slate-600">#{tableData.number}</Badge>
                </div>
                <div className="grid gap-4 lg:grid-cols-3">
                  {['starter', 'main', 'dessert'].map(type => (
                    <div key={type} className="p-3 rounded-lg border border-slate-700 bg-slate-900/40">
                      <div className="flex gap-2 items-center mb-2">
                        <Utensils size={16} className="text-blue-400" />
                        <div className="font-semibold text-white">
                          {type === 'starter' ? 'Entrées' : type === 'main' ? 'Plats' : 'Desserts'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {(groups[type] || []).map(d => (
                          <div key={d.name} className="flex gap-2 justify-between items-center">
                            <div className="min-w-0">
                              <div className="flex gap-2 items-center">
                                <div className="font-medium text-slate-200">{d.name}</div>
                                {typeof d.price === 'number' && (
                                  <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                    {formatEUR(d.price)}
                                  </Badge>
                                )}
                              </div>
                              {d.description && <div className="text-sm text-slate-400">{d.description}</div>}
                            </div>
                            <div className="flex gap-2 items-center">
                              <Input
                                type="number"
                                min="0"
                                value={quantities[d.name] || ''}
                                onChange={(e) => setQty(d.name, e.target.value)}
                                className="w-20 text-white bg-slate-900 border-slate-700 placeholder-slate-400"
                              />
                            </div>
                          </div>
                        ))}
                        {(groups[type] || []).length === 0 && (
                          <div className="text-sm text-slate-400">Aucun élément</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block mb-1 text-sm text-slate-300">Commentaire (allergies, préférences, etc.)</label>
                    <Textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Ex: Allergie aux noix, pas trop épicé, etc."
                      className="min-h-[80px] text-white bg-slate-900 border-slate-700 placeholder-slate-400"
                    />
                  </div>
                  <div className="flex gap-2 items-center">
                    <Button
                      disabled={submitting}
                      onClick={submitOrder}
                      className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    >
                      Commander
                    </Button>
                    {submitted && (
                      <Badge className="inline-flex gap-1 items-center text-green-300 bg-green-500/20 border-green-500/30">
                        <CheckCircle size={12} />
                        Envoyée
                      </Badge>
                    )}
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
      <Footer socialLinks={socialLinks} />
    </div>
  );
};

export default TableOrder;
